"use client";
import { gsap } from "@/lib/gsap";
import Image from "next/image";
import { useEffect, useRef } from "react";
import FeatureItem from "./FeatureItem";

interface Feature {
  title: string;
  description: string;
}

interface ImageData {
  src: string;
  alt: string;
}

interface WhyChooseUsData {
  tagline?: string;
  title?: string;
  description?: string;
  image?: ImageData;
  features?: Feature[];
}

export default function WhyChooseUs({ data }: { data?: WhyChooseUsData }) {
  const { tagline, title, description, image, features } = data || {};
  const typedFeatures: Feature[] = features || [];
  const typedImage: ImageData | undefined = image;

  const headerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!headerRef.current || !imageRef.current) return;

    const ctx = gsap.context(() => {
      const elements = headerRef.current?.querySelectorAll("[data-animate]");

      if (elements) {
        gsap.fromTo(
          elements,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          }
        );

        gsap.fromTo(
          imageRef.current,
          {
            opacity: 0,
            y: 20,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: imageRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, [headerRef, imageRef]);

    return () => ctx.revert();
  }, []);

  return (
    <section className="bg-white py-[70px]">
      <div className="container mx-auto px-4">
        {}

        <div className="text-start" ref={headerRef}>
          {tagline && (
            <div data-animate className="flex items-center mb-4">
              <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
              <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                {tagline}
              </p>
            </div>
          )}

          {title && (
            <h2
              data-animate
              className="text-4xl md:text-5xl font-bold text-theme-secondary capitalize"
            >
              {title}
            </h2>
          )}

          {description && (
            <p data-animate className="mt-4 text-lg text-theme-secondary-light">
              {description}
            </p>
          )}
        </div>

        {}
        {typedImage && typedImage.src && (
          <div
            ref={imageRef}
            className="my-10 lg:my-12 p-4 bg-[#F7F7F7] rounded-[12px] mx-auto min-h-64"
          >
            <Image
              src={typedImage.src}
              alt={typedImage.alt || "Feature image"}
              width={1200}
              height={400}
              quality={85}
              className="rounded-lg w-full !h-64 md:h-80 lg:h-96 object-cover"
            />
          </div>
        )}

        {}
        {typedFeatures && typedFeatures.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {typedFeatures.map((feature, index) => (
              <FeatureItem key={index} feature={feature} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
